/** @file splitCuts: the slot images an exploded sheet's targeted islands fill (cut, scaled, pivoted). */
import { islandImage } from '@zilo/pose3d-image-io/island/islandImage';
import type { IslandMap } from '@zilo/pose3d-image-io/island/islandTypes';
import type { Image } from '@zilo/pose3d-image-io/types/image';
import { fitIsland } from './fitIsland';
import type { IslandTarget } from './proposeTargets';
import type { CutPart } from './sheetTypes';

/**
 * One cut per island with a part: the island's own pixels (`islandImage`), fitted into the part's slot with its pivot
 * on the slot pivot (`fitIsland`, `nearest` or `linear` by the profile's sampling). `duplicates` names the slots more
 * than one island targets; those are left out until the choice is fixed.
 */
export function splitCuts(sheet: Image, map: IslandMap, targets: readonly IslandTarget[], mode: 'nearest' | 'linear'): { cuts: CutPart[]; duplicates: string[] } {
  const count = new Map<string, number>();
  for (const t of targets) if (t.part !== null) count.set(t.part.slot, (count.get(t.part.slot) ?? 0) + 1);
  const duplicates = [...count].filter(([, n]) => n > 1).map(([slot]) => slot);
  const cuts: CutPart[] = [];
  for (const t of targets) {
    if (t.part === null || duplicates.includes(t.part.slot)) continue;
    const { image, clipped } = fitIsland(islandImage(sheet, map, t.island), t.pivot, t.part, mode);
    let pixels = 0;
    for (let i = 3; i < image.data.length; i += 4) if ((image.data[i] ?? 0) > 0) pixels += 1;
    cuts.push({ drawable: t.part.drawable, slot: t.part.slot, image, pixels, clipped });
  }
  return { cuts, duplicates };
}
